import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ModuleType } from '@fi-shared/types/module-type';
import { AppTextService } from '@fi-shared/app-text';
import { FiTagsListPositionTypes, FiTagsSelectionTypes } from '../components/items-list/enums';
import { SortByProperty } from '@fi-shared/utils/sort-by-property';
import { FiImageSize } from '@fi-shared/types/image-size';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ISiteDesign } from '@fi-shared/models';
import { fiWidgetGetTextId } from '@fi-shared/widget/widget-registry';
import { WidgetModel } from '@fi-app/widget/model/widget.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { WidgetAdministrateService } from '@fi-app/widget/widget-administrate.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { TabsModuleService } from '@fi-app/utilities/tabs/tabs-module.service';
import { WidgetAdministrateModel } from '@fi-app/widget/model/widget-administrate.model';
import { ListViewSettingsService } from '@fi-app/utilities/fi-services/list-view-settings.service';
import { ListViewSettingModel } from '@fi-app/components/items-list/list-view-setting.model';
import { WidgetService } from '@fi-app/widget/widget.service';
import { FiCustomerSettingsService } from '@fi-app/appearance/customer-settings/customer-settings.service';
import { FiAppSettings } from '@fi-shared/fi-app-settings';

interface IAppearanceWidget {
    Key: string;
    Value: number;
}
// not used component
@Component({
    selector: 'fi-appearance-view',
    templateUrl: './appearance-view.component.html',
    styleUrls: ['./appearance-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-appearance-view' },
    standalone: false,
})
export class FiAppearanceViewComponent implements OnInit, OnDestroy {
    ModuleType = ModuleType;
    FiStateNames = FiStateNames;

    ListViewSettings: ListViewSettingModel[] = [];
    homePageWidgets: WidgetModel[] = [];
    WidgetAdministrate: WidgetAdministrateModel;
    WidgetAdministrateWidgets: { [prop: string]: WidgetModel };
    widgets: IAppearanceWidget[] = [];
    customerSettings: ISiteDesign;

    loading$ = new BehaviorSubject(true);

    constructor(
        private appTextService: AppTextService,
        private widgetAdministrateService: WidgetAdministrateService,
        private mediaStorageService: FiMediaStorageService,
        private tabsModuleService: TabsModuleService,
        private listViewSettingsService: ListViewSettingsService,
        private widgetService: WidgetService,
        private fiCustomerSettingsService: FiCustomerSettingsService,
    ) {
        this.widgetAdministrateService.previewImageGenerated$.takeUntil(this.ngOnDestroy$).subscribe(() => {
            this.WidgetAdministrate.ChangedOn = new Date().toISOString();
        });
    }

    ngOnInit() {
        this.getWidgetsAndAppearanceData();

        this.tabsModuleService.setTabsSettings({
            listData: undefined,
        });
    }

    private getWidgetsAndAppearanceData() {
        const promises: Promise<void>[] = [];
        promises.push(
            new Promise<void>(async (resolve, reject) => {
                await this.getHomePageWidgets();
                await this.getWidgetAdministrateWidgets();
                resolve();
            }),
        );
        promises.push(this.getWidgets());
        promises.push(this.getCustomerSettings());
        promises.push(this.getListViewSettings());
        Promise.all(promises).finally(() => this.loading$.next(false));
    }

    private async getHomePageWidgets(): Promise<void> {
        return this.widgetService.getWidgets(ObjectTypes.FirstPage).then((widgets: WidgetModel[]) => {
            this.homePageWidgets = widgets;
        });
    }

    private async getWidgetAdministrateWidgets(): Promise<void> {
        return this.widgetAdministrateService
            .getWidgetAdministrateForModule(ModuleType.FirstPage)
            .then((result: WidgetAdministrateModel) => {
                this.WidgetAdministrate = result;
                if (!this.WidgetAdministrate.BaseUrl) {
                    this.widgetAdministrateService.InitAdministrateWidgetDefaultLayout(
                        this.WidgetAdministrate,
                        this.homePageWidgets,
                    );
                }
                this.WidgetAdministrateWidgets = this.widgetAdministrateService.BuildWidgetAdministrateWidgetsList(
                    this.WidgetAdministrate,
                    this.homePageWidgets,
                );
            });
    }

    private getWidgets(): Promise<void> {
        return this.widgetService.GetWidgetNameTextIds().then((result: IAppearanceWidget[]) => {
            this.widgets = result;
        });
    }

    private getCustomerSettings(): Promise<void> {
        return this.fiCustomerSettingsService.getCustomerSettings().then((data: ISiteDesign) => {
            this.customerSettings = data;
        });
    }

    private getListViewSettings(): Promise<void> {
        return this.listViewSettingsService
            .getSettings([ObjectTypes.Document, ObjectTypes.MediaBank, ObjectTypes.User])
            .then((listViewSettings: ListViewSettingModel[]) => {
                this.ListViewSettings = listViewSettings;
            });
    }

    getWidgetTexts(moduleId): string {
        return this.getModuleWidgetNames(moduleId)
            .map((textId: string) => this.appTextService.get(textId))
            .join(', ');
    }

    getListViewPositionText(): string {
        return SortByProperty(this.ListViewSettings, 'ObjectType', true)
            .map((setting) => {
                return String.format(
                    '{0}: {1};',
                    this.getAppText(setting.ObjectType),
                    this.appTextService.get(
                        setting.PositionType === FiTagsListPositionTypes.Top ? 'common.top' : 'common.left',
                    ),
                );
            })
            .join(' ');
    }

    getListedTagsText(setting): string {
        if (setting.ListType == FiTagsSelectionTypes.AllTags)
            return this.appTextService.get('ccmAppearance.listView.allTags');

        return SortByProperty(setting.ListViewTags, setting.getOrderBy())
            .map((tag) => tag.Name)
            .join(', ');
    }

    getListViewListedTagsText(): string {
        return SortByProperty(this.ListViewSettings, 'ObjectType', true)
            .map((setting) => {
                return String.format('{0}: {1};', this.getAppText(setting.ObjectType), this.getListedTagsText(setting));
            })
            .join(' ');
    }

    private getModuleWidgetNames(moduleId: number): string[] {
        const items: IAppearanceWidget[] = this.widgets.filter((item: IAppearanceWidget) => item.Value === moduleId);
        return items.map((item: IAppearanceWidget) => item.Key);
    }

    getStartPageWidgetsNames(): string {
        return Object.keys(this.WidgetAdministrateWidgets)
            .map((key: string) => {
                const widget: WidgetModel = this.WidgetAdministrateWidgets[key];
                return this.appTextService.get(fiWidgetGetTextId(widget.WidgetType));
            })
            .join(', ');
    }

    getWidgetAdministratePreviewFileUrl(WidgetAdministrate): string {
        return (
            this.mediaStorageService.getMediaFileUrlByFileName(
                WidgetAdministrate.PreviewFileName + FiAppSettings.DefaultMediaBankImageExtension,
                FiImageSize.Small,
            ) +
            '?ChangedOn=' +
            this.WidgetAdministrate.ChangedOn
        );
    }

    getAppText(objectType): string {
        if (objectType == ObjectTypes.Document) return this.appTextService.get('common.pages');

        if (objectType == ObjectTypes.MediaBank) return this.appTextService.get('common.files');

        return this.appTextService.get('common.our_organisation');
    }

    getCustomerSettingsImagesText(): string {
        if (!this.customerSettings) {
            return '';
        }

        const items: string[] = [];
        if (this.customerSettings.LogoReference) {
            items.push(this.appTextService.get('customer_settings.logo'));
        }

        if (this.customerSettings.LandscapeReference) {
            items.push(this.appTextService.get('customer_settings.landscape'));
        }

        return items.join(', ');
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
